import * as React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AuditModal } from "../../auditmodal/components/AuditModal";
import {
    DcfRequestStatusEnum,
    DcfSystemWorkflow,
    CorrectionTypesEnum
} from "../../misc/api/apiTypes";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import { DataCorrectionStatus } from "../List/DataCorrectionStatus";
import { CorrectionMetadata } from "./CorrectionMetadata";
import { DataCorrectionApproveReject } from "./DataCorrectionApproveReject";
import { DataCorrectionProgressBar } from "./DataCorrectionProgressBar";
import { ReviewItem } from "./ReviewItem";
import FileAttachmentsViewer from "../../file-attachments/components/viewer/FileAttachmentsViewer";
import { AttachmentRelatesTo } from "../../file-attachments/models";
import { Authorization } from "../../misc/components/Authorization";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { CorrectionPreviewPanel } from "../Execution/CorrectionsPreviewPanel";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Correction, VisibleWizards } from '../Request/DcfRequestReducer';
import { WebPortalClient, DcfRequest, DcfRequestStatus, ApiException } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Condition from "../../misc/components/Condition";
import { ForwardProps, useSpring } from "@react-spring/web";

export interface DataCorrectionRequestViewProps {
}

export interface ApprovalState {
    type: string;
    isSelected: boolean;
    isApproved?: boolean;
}

export interface DataCorrectionRequestViewState {
    selectedApproval: null | "site" | "cra" | "vitalograph" | "actioned" | "implementation";
    isApproved: boolean;
}

interface ReviewAnimation {
    siteApproved: boolean;
    actioned: boolean;
    vitalographApproved: boolean;
}

export const DataCorrectionRequestView = ({ dcfRequestId = null, onClose = null }) => {
    const params = useParams<{ dcfRequestId: string }>();
    var navigate = useNavigate();

    dcfRequestId = dcfRequestId ?? params.dcfRequestId;
    if (onClose == undefined) {
        onClose = () => {
            navigate('/Dcf');
        }
    }

    const [selectedApproval, setSelectedApproval] = React.useState<DataCorrectionRequestViewState>(
        null
    );
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [systemWorkflow, setSystemWorkflow] = React.useState<DcfSystemWorkflow>();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [isHighlightingStatus, setIsHighlightingStatus] = React.useState<ReviewAnimation>({
        siteApproved: false,
        actioned: false,
        vitalographApproved: false
    });

    const client = useWebPortalApiClient();
    const [dcfRequest, setDcfRequest] = React.useState<DcfRequest>();
    const [statuses, setStatuses] = React.useState<DcfRequestStatus[]>();
    const [labels, setLabels] = React.useState<string[]>();
    const [verificationApprovalEnabled, setVerificationApprovalEnabled] = React.useState(false);

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions, currentUser } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();
    const getDcfRequest = async (id: string) => {
        try {
            const dcfRequestResponse = await client.client.getDcfRequest(id, undefined, "corrections,site,subject,visit,session");
            setDcfRequest(dcfRequestResponse);
        } catch (ex) {
            if ((ex as ApiException).status === 404) {
                setErrorMessage("The Data Correction cannot be found");
                return;
            }
            setErrorMessage("Error loading the Data Correction");
        }
    }

    React.useEffect(
        () => {
            if (dcfRequestId == null) return;
            const loadDcfRequest = async () => {
                const craEnabled = await legacyClient.getSetting("dcf.configuration.verificationApprovalEnabled", study.id);
                setVerificationApprovalEnabled(craEnabled);

                const dcfSettings = await legacyClient.getStudyDcfSettings(study.id)
                setSystemWorkflow(dcfSettings.SystemWorkflow);

                const statusesResponse = await client.client.getDcfRequestStatuses(undefined, craEnabled ? undefined : `id ne '${DcfRequestStatusEnum.VerificationReview}'`);
                setStatuses(statusesResponse.value);

                const dcfLabelsResponse = await client.client.getDcfRequests("label");
                setLabels(dcfLabelsResponse.value.map(l => l.label));

                getDcfRequest(dcfRequestId);
            };

            loadDcfRequest();
        },
        [dcfRequestId, study.id]
    );

    const onSiteReview = async (requestId: string, isApproved: boolean, annotation: string) => {
        setSubmitting(true);
        client.client.patchDcfRequest({
            id: requestId,
            siteApprovedByUser: currentUser.Email,
            siteApproved: isApproved,
            siteApprovedAnnotation: annotation,
        }, requestId, annotation)
            .then(async _ => {
                setAuditModalVisible(false);
                await getDcfRequest(requestId);
                setTimeout(
                    () => onReviewComplete(DcfRequestStatusEnum.SiteApproval, isApproved),
                    2000
                );
                setIsHighlightingStatus({
                    siteApproved: false,
                    actioned: false,
                    vitalographApproved: false
                });
            });
    }

    const onVitalographReview = async (requestId: string, isApproved: boolean, annotation: string) => {
        setSubmitting(true);
        client.client.patchDcfRequest({
            id: requestId,
            verificationReviewedByUser: currentUser.Email,
            verificationReviewed: isApproved,
            verificationReviewedAnnotation: annotation,
        }, requestId, annotation)
            .then(async _ => {
                setAuditModalVisible(false);
                await getDcfRequest(requestId);
                setTimeout(
                    () => onReviewComplete(DcfRequestStatusEnum.VitalographApproval, isApproved),
                    2000
                );
                setIsHighlightingStatus({
                    siteApproved: false,
                    actioned: false,
                    vitalographApproved: false
                });
            });
    }

    const onActioned = async (requestId: string, status: DcfRequestStatusEnum, isApproved: boolean, annotation: string) => {
        setSubmitting(true);
        await client.client.patchDcfRequest({
            id: requestId,
            actionedByUser: currentUser.Email,
            actionedApproved: isApproved,
            actionedApprovedAnnotation: annotation,
            implementedByUser: status === DcfRequestStatusEnum.Implemented ? currentUser.Email : undefined,
            implementedAnnotation: status === DcfRequestStatusEnum.Implemented ? annotation : undefined,
        }, requestId, annotation)
            .then(async _ => {
                setAuditModalVisible(false);
                await getDcfRequest(requestId);
                setTimeout(
                    () => onReviewComplete(status, isApproved),
                    2000
                );
                setIsHighlightingStatus({
                    siteApproved: false,
                    actioned: false,
                    vitalographApproved: false
                });
            }).catch((ex) => {
                setErrorMessage(ex.message);
                setAuditModalVisible(false);
                setSubmitting(false);
            });
    }

    const onImplemented = async (request: DcfRequest, status: DcfRequestStatusEnum, annotation: string) => {
        setSubmitting(true);
        await client.client.patchDcfRequest({
            id: request.id,
            implementedByUser: currentUser.Username,
            implementedAnnotation: annotation,
            clientImplemented: systemWorkflow === DcfSystemWorkflow.Dcf360 && dcfRequest.correctionTypeId === CorrectionTypesEnum.Miscellaneous ? true : false
        }, request.id, annotation)
            .then(async _ => {
                setAuditModalVisible(false);
                await getDcfRequest(request.id);
                setTimeout(
                    () => onReviewComplete(status, true),
                    2000
                );
                setIsHighlightingStatus({
                    siteApproved: false,
                    actioned: false,
                    vitalographApproved: false
                });
            })
            .catch((ex) => {
                setErrorMessage(ex.message);
                setAuditModalVisible(false);
                setSubmitting(false);
            });
    }

    const onReviewComplete = (statusType: DcfRequestStatusEnum, isApproved: boolean) => {
        if (statusType === DcfRequestStatusEnum.SiteApproval) {
            setIsHighlightingStatus({
                siteApproved: true,
                actioned: false,
                vitalographApproved: false
            });
            setSubmitting(false);
        }

        if (statusType === DcfRequestStatusEnum.VitalographApproval) {
            setIsHighlightingStatus({
                siteApproved: false,
                actioned: false,
                vitalographApproved: true
            });
            setSubmitting(false);
        }

        if (statusType === DcfRequestStatusEnum.Approved) {
            setIsHighlightingStatus({
                siteApproved: false,
                actioned: true,
                vitalographApproved: false
            });
            setSubmitting(false);
        }

        if (statusType === DcfRequestStatusEnum.Implemented) {
            setIsHighlightingStatus({
                siteApproved: false,
                actioned: true,
                vitalographApproved: false
            });
            setSubmitting(false);
        }
    }

    const onAuditForm = async (auditForm) => {
        switch (selectedApproval.selectedApproval) {
            case "site":
                await onSiteReview(dcfRequest.id, selectedApproval.isApproved, auditForm.Annotation);
                break;
            case "vitalograph":
                await onVitalographReview(dcfRequest.id, selectedApproval.isApproved, auditForm.Annotation);
                break;
            case "actioned":
                await onActioned(dcfRequest.id, systemWorkflow === DcfSystemWorkflow.Implementation ? DcfRequestStatusEnum.Implemented : DcfRequestStatusEnum.Approved, selectedApproval.isApproved, auditForm.Annotation);
                break;
            case "implementation":
                await onImplemented(dcfRequest, DcfRequestStatusEnum.Implemented, auditForm.Annotation);
                break;
            default:
                return;
        }
    };

    interface ScrollSpringProps { y: number };

    const [_, setY] = useSpring<ForwardProps<ScrollSpringProps>>(() => ({
        immediate: false,
        y: window.scrollY,
        onChange: (props: ScrollSpringProps) => {
            window.scroll(0, props.y);
        }
    }));

    if (!dcfRequest) {

        if (errorMessage) {
            return <Alert show={errorMessage !== null} category={AlertCategoryEnum.Danger}>
                {errorMessage}
            </Alert>
        }

        return <LoadingWithMessage message="Loading Data Correction..." />;
    }

    const title = `${dcfRequest.label} - View Data Correction`;

    const statusesWithCreated = [{ id: "DCF_PSEUDO_CREATED", name: "Created", order: 0 } as DcfRequestStatus].concat(
        statuses
    );
    const allStatuses: DcfRequestStatus[] = statusesWithCreated.filter(x => x.id !== DcfRequestStatusEnum.Rejected)
        .sort((a, b) => a.order - b.order);

    if (errorMessage !== null) {
        setY({ y: 0 })
    }

    const isSiteApproved = (): boolean => {
        return !!dcfRequest.verificationReviewedByUser || !!dcfRequest.actionedByUser || (!!dcfRequest.siteApprovedByUser && dcfRequest.status !== DcfRequestStatusEnum.Rejected);
    }

    const isVitalographApproved = (): boolean => {
        return !!dcfRequest.actionedByUser || (!!dcfRequest.verificationReviewedByUser && dcfRequest.status !== DcfRequestStatusEnum.Rejected);
    }

    const isActionApproved = (): boolean => {
        return !!dcfRequest.actionedByUser && dcfRequest.status !== DcfRequestStatusEnum.Rejected;
    }

    const onReturnToList = () => {
        setDcfRequest(null);
        if (onClose != undefined)
            onClose();
    }
    return dcfRequestId == null ? null : (
        <DocumentTitle title={`${title} | Vitalograph Web Portal`}>
            <>
                <Alert show={errorMessage !== undefined} category={AlertCategoryEnum.Danger}>
                    {errorMessage}
                </Alert>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className="text-primary">{dcfRequest.label} <DataCorrectionStatus value={dcfRequest.status as DcfRequestStatusEnum} /></h2>
                        <h5 className="text-muted">View Data Correction</h5>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12 box">
                        {dcfRequest.status !== DcfRequestStatusEnum.Rejected ? (
                            <DataCorrectionProgressBar
                                statuses={allStatuses}
                                currentStatusId={dcfRequest.status}
                                systemWorkflow={systemWorkflow}
                                siteId={dcfRequest.siteId}
                            />
                        ) : null}
                    </div>
                </div>
                <div id="dcfViewCore" className="row">
                    <div className="col-md-12">
                        <div className="panel panel-default">
                            <div className="panel-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <CorrectionMetadata
                                            principalInvestigator={
                                                dcfRequest?.site?.contact != null
                                                    ? dcfRequest.site.contact
                                                    : "Not Specified"
                                            }
                                            site={
                                                dcfRequest.site != null
                                                    ? dcfRequest.site.number
                                                    : "All"
                                            }
                                            subject={
                                                dcfRequest.subject != null
                                                    ? dcfRequest.subject.number
                                                    : "All"
                                            }
                                            visit={dcfRequest.visit != null
                                                ? dcfRequest.visit.name : "All"}
                                            session={dcfRequest.session != null ? dcfRequest.session.name : "All"}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <div className="row">
                                            <div className="col-sm-3">
                                                <h4>Request Raised By</h4>
                                            </div>
                                            <div className="col-sm-9 pull-right">
                                                {dcfRequest.createdByUser}
                                                <br />
                                                <LocalizedDateTime
                                                    time={dcfRequest.created}
                                                    accuracy={DateTimeAccuracy.Second}
                                                    isTooltip={true}
                                                />
                                            </div>
                                        </div>
                                        <hr />
                                        {dcfRequest.siteId ? <><ReviewItem
                                            currentRequestStatus={dcfRequest.status as DcfRequestStatusEnum}
                                            username={dcfRequest.siteApprovedByUser}
                                            datetime={dcfRequest.siteApprovedDateTime}
                                            reason={dcfRequest.siteApprovedAnnotation}
                                            stepApproved={isSiteApproved()}
                                            title={"Site Approval"}
                                        /><hr /></> : null}
                                        {verificationApprovalEnabled ? <><ReviewItem
                                            currentRequestStatus={dcfRequest.status as DcfRequestStatusEnum}
                                            username={dcfRequest.verificationReviewedByUser}
                                            datetime={dcfRequest.verificationReviewedDateTime}
                                            reason={dcfRequest.verificationReviewedAnnotation}
                                            stepApproved={isVitalographApproved()}
                                            title={"CRA Approval"}
                                        /><hr /></> : null}
                                        <ReviewItem
                                            currentRequestStatus={dcfRequest.status as DcfRequestStatusEnum}
                                            username={dcfRequest.actionedByUser}
                                            datetime={dcfRequest.actionedApprovedDateTime}
                                            reason={dcfRequest.actionedApprovedAnnotation}
                                            stepApproved={isActionApproved()}
                                            title={"Vitalograph Approval"}
                                        />
                                        <hr />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <CorrectionPreviewPanel
                    correctionType={dcfRequest.correctionTypeId}
                    corrections={dcfRequest.corrections.map((x): Correction => ({
                        sourceCorrection: VisibleWizards.Misc,
                        correctionTypeId: CorrectionTypesEnum.Miscellaneous,
                        entity: x.entity,
                        field: x.field,
                        oldValue: x.originalValue,
                        newValue: x.correctedValue
                    }))}
                    miscCorrection={{
                        fromDetails: dcfRequest.originalEntry,
                        toDetails: dcfRequest.newEntry
                    }}
                />
                {dcfRequest.additionalComments ?
                    <div className="row">
                        <div className="col-md-12">
                            <div className="panel panel-default">
                                <div className="panel-heading">
                                    <h3 className="panel-title">Comments / Reason for Data Correction</h3>
                                </div>
                                <div className="panel-body">
                                    <p>{dcfRequest.additionalComments}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}
                <div className="row">
                    <Authorization
                        permissionId={PermissionEnum.DownloadDcfAttachments}
                        currentPermissions={currentUserPermissions}
                        studyId={study.id}
                    >
                        <FileAttachmentsViewer
                            title="File Attachments"
                            attachmentRelatesTo={AttachmentRelatesTo.DcfRequest}
                            relatedEntityId={dcfRequest.entityId}
                        />
                    </Authorization>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <DataCorrectionApproveReject
                            studyId={study.id}
                            permissionId={PermissionEnum.SiteApproveDataCorrections}
                            permissions={currentUserPermissions}
                            currentRequestStatusId={dcfRequest.status}
                            requiredStatus={DcfRequestStatusEnum.SiteApproval}
                            isAuditModalVisible={auditModalVisible}
                            title="Site Approval"
                        >
                            <button
                                id="approveButton"
                                className="btn btn-success"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "site",
                                        isApproved: true
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Approve Data Correction
                            </button>
                            &nbsp;
                            <button
                                id="rejectButton"
                                className="btn btn-danger"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "site",
                                        isApproved: false
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Reject Data Correction
                            </button>
                        </DataCorrectionApproveReject>
                        <DataCorrectionApproveReject
                            studyId={study.id}
                            permissionId={PermissionEnum.CraApprovalDataCorrections}
                            permissions={currentUserPermissions}
                            currentRequestStatusId={dcfRequest.status}
                            requiredStatus={DcfRequestStatusEnum.VerificationReview}
                            isAuditModalVisible={auditModalVisible}
                            title="CRA Approval"
                        >
                            <button
                                id="approveButton"
                                className="btn btn-success"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "vitalograph",
                                        isApproved: true
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Approve Data Correction
                            </button>
                            &nbsp;
                            <button
                                id="rejectButton"
                                className="btn btn-danger"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "vitalograph",
                                        isApproved: false
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Reject Data Correction
                            </button>
                        </DataCorrectionApproveReject>
                        <DataCorrectionApproveReject
                            studyId={study.id}
                            permissionId={PermissionEnum.DataCorrectionApproveImplementation}
                            permissions={currentUserPermissions}
                            currentRequestStatusId={dcfRequest.status}
                            requiredStatus={DcfRequestStatusEnum.VitalographApproval}
                            isAuditModalVisible={auditModalVisible}
                            title="Implementation Approval"
                        >
                            <button
                                id="approveButton"
                                className="btn btn-success"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "actioned",
                                        isApproved: true
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Approve Data Correction Implementation
                            </button>
                            &nbsp;
                            <button
                                id="rejectButton"
                                className="btn btn-danger"
                                disabled={auditModalVisible || submitting}
                                onClick={(): void => {
                                    setSelectedApproval({
                                        selectedApproval: "actioned",
                                        isApproved: false
                                    });
                                    setAuditModalVisible(true);
                                }}
                            >
                                Cannot Implement
                            </button>
                        </DataCorrectionApproveReject>
                        <Condition test={() => systemWorkflow === DcfSystemWorkflow.Log}>
                            <DataCorrectionApproveReject
                                studyId={study.id}
                                permissionId={PermissionEnum.DataCorrectionApproveImplementation}
                                permissions={currentUserPermissions}
                                currentRequestStatusId={dcfRequest.status}
                                requiredStatus={DcfRequestStatusEnum.Approved}
                                isAuditModalVisible={auditModalVisible}
                                title="Implementation Approval"
                            >
                                <button
                                    id="implementButton"
                                    className="btn btn-success"
                                    disabled={auditModalVisible || submitting}
                                    onClick={(): void => {
                                        setSelectedApproval({
                                            selectedApproval: "implementation",
                                            isApproved: true
                                        });
                                        setAuditModalVisible(true);
                                    }}
                                >
                                    Mark as Implemented
                                </button>
                                &nbsp;
                            </DataCorrectionApproveReject>
                        </Condition>
                        <Condition test={() => systemWorkflow === DcfSystemWorkflow.Dcf360 && dcfRequest.correctionTypeId === CorrectionTypesEnum.Miscellaneous}>
                            <DataCorrectionApproveReject
                                studyId={study.id}
                                permissionId={PermissionEnum.DataCorrectionApproveImplementation}
                                permissions={currentUserPermissions}
                                currentRequestStatusId={dcfRequest.status}
                                requiredStatus={DcfRequestStatusEnum.Approved}
                                isAuditModalVisible={auditModalVisible}
                                title="Mark as Client Implemented"
                            >
                                <button
                                    id="clientImplementedButton"
                                    className="btn btn-success"
                                    disabled={auditModalVisible || submitting}
                                    onClick={(): void => {
                                        setSelectedApproval({
                                            selectedApproval: "implementation",
                                            isApproved: true,
                                        });
                                        setAuditModalVisible(true);
                                    }}
                                >
                                    Mark as Client Implemented
                                </button>
                                &nbsp;
                            </DataCorrectionApproveReject>
                        </Condition>
                    </div>
                    <div className="col-md-12 h2 text-right">
                        <button id="returnButton" onClick={() => onReturnToList()} className="btn btn-primary btn-outline">
                            Return to List
                        </button>
                    </div>
                </div>
                <br />
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={onAuditForm}
                    />
                ) : null}
            </>
        </DocumentTitle>
    );
};
